/**
 * Дюймы в миллиметры
 * ====================================
 */
import { LoaderFunctionArgs, MetaDescriptor, MetaFunction, useLoaderData, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";
import H2 from "~/components/H2";
import H3 from "~/components/H3";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Author from "~/components/tool-page/Author";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import LengthCalculator, { Unit } from "~/components/LengthCalculator";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";

const CATEGORY = "length";
const TKEY = "calculators.length.inches_to_mm";
const ROUTE_ENDING = "inches-to-mm";
const LAST_UPDATED = new Date("2024-11-30T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = function({ data }) {
  invariant(data);

  const meta: MetaDescriptor[] = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createArticleSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov
    )
  ];

  if (data.meta.shouldIndex === false) {
    meta.push({ name: "robots", content: "noindex" });
  }

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  return meta;
};


export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "pt", "tr", "it", "pl", "ar", "th"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";
  const url = new URL(request.url);
  const number: number | null = params.number ? Number(params.number) : null;

  const t = await getTranslator(request);

  // Calculate result if number is set
  let error: string | null = null;
  let result: number | null = null;
  let notRoundedResult: number | null = null;
  let pluralizationCount = number ?? 0;

  if (number !== null) {
    if (Number.isNaN(number) || number < 0) {
      error = t(`${TKEY}.error_invalid_number`);
    } else {
      result = inchesToMm(number, true);
      notRoundedResult = inchesToMm(number, false);
    }

    if (number !== Math.floor(number)) {
      if (number < 2) {
        pluralizationCount = 2;
      } else {
        pluralizationCount = Math.floor(number);
      }
    }
  }
  const title = number
    ? t(`${TKEY}.title_for_number`, { count: pluralizationCount, number })
    : t(`${TKEY}.title`);

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  let shouldIndex = false;

  const ammoCalibers = [0.17, 0.204, 0.22, 0.223, 0.243, 0.270, 0.30, 0.30, 0.308, 0.338, 0.375, 0.45, 0.50, 0.25, 0.32, 0.357, 0.38, 0.40, 0.44, 0.45, 0.50, 0.410, 0.300, 0.357];

  if (
    // Root page
    number === null
    // // .025 increments
    // || (number >= 0 && number <= 20 && number * 1000 % 25 === 0)
    // // 0.02 increments
    // || (number >= 0 && number <= 20 && number * 100 % 2 === 0)
    // || (number >= 0 && number <= 100 && number % 1 === 0)
    // || ammoCalibers.includes(number)

  ) {
    shouldIndex = true;
  }

  const mainCalculatorURL = url.protocol + "//" + url.host + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING;

  return {
    number,
    result,
    notRoundedResult,
    error,
    meta: {
      locale,
      title,
      description: number !== null
        ? t(`${TKEY}.description_for_number`, { number, tv: "", result: result }) // TODO tv
        : t(`${TKEY}.description`),
      breadcrumbs,
      /**
       * Results page should have a canonical link to the main calculator page.
       * Attempt to glue links juice from results page to the main calculator page,
       * and avoid Google spam content filter
       */
      canonical:  request.url === mainCalculatorURL ? null : mainCalculatorURL,
      shouldIndex
    }
  };
};

function inchesToMm(number: number, round = true): number {
  const result = number * 25.4;
  return round ? Math.round(result * 10) / 10 : result;
}

export default function Index() {
  const {
    meta,
    number,
    result,
    notRoundedResult,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);

  const commonValues = [
    0.5,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    19.685,
    39.37,
    40,
    50,
    60,
    70,
    80,
    90,
    100,
    500,
    1000
  ];
  const formulaSvg = <div className="bg-gray-300 rounded-md p-6 w-[200px] text-black flex justify-center">
    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="158"
         height="35">
      <defs>
        <clipPath id="QQKJymvVClGo">
          <path fill="none" stroke="none" d=" M 0 0 L 158 0 L 158 35 L 0 35 L 0 0 Z" />
        </clipPath>
      </defs>
      <g transform="scale(1,1)" clipPath="url(#QQKJymvVClGo)">
        <g transform="translate(-59,-239)">
          <g transform="scale(21,21)">
            <g transform="translate(3.142857142857143,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.088 -0.059000000000000004 L 0.088 -0.059000000000000004 L 0.146 -0.29 Q 0.162 -0.354 0.162 -0.374 Q 0.162 -0.42 0.131 -0.42 Q 0.09 -0.42 0.064 -0.325 Q 0.061 -0.314 0.058 -0.301 Q 0.047 -0.279 0.041 -0.278 Q 0.029 -0.278 0.029 -0.28800000000000003 Q 0.029 -0.304 0.048 -0.356 Q 0.054 -0.373 0.06 -0.385 Q 0.084 -0.434 0.12 -0.441 Q 0.127 -0.442 0.134 -0.442 Q 0.188 -0.442 0.214 -0.397 Q 0.224 -0.378 0.226 -0.355 Q 0.29 -0.442 0.382 -0.442 Q 0.433 -0.442 0.464 -0.41600000000000004 Q 0.492 -0.392 0.496 -0.34500000000000003 Q 0.5640000000000001 -0.442 0.659 -0.442 Q 0.748 -0.442 0.769 -0.373 L 0.769 -0.372 Q 0.774 -0.355 0.774 -0.335 Q 0.774 -0.28200000000000003 0.713 -0.115 Q 0.6950000000000001 -0.07100000000000001 0.6950000000000001 -0.044 Q 0.6950000000000001 -0.011 0.719 -0.011 Q 0.781 -0.011 0.8170000000000001 -0.121 L 0.8210000000000001 -0.135 Q 0.8260000000000001 -0.151 0.8310000000000001 -0.152 L 0.8310000000000001 -0.153 L 0.836 -0.153 Q 0.848 -0.153 0.848 -0.14300000000000002 Q 0.848 -0.126 0.8270000000000001 -0.08 Q 0.784 0.011 0.717 0.011 Q 0.666 0.011 0.644 -0.032 Q 0.635 -0.05 0.635 -0.07100000000000001 Q 0.635 -0.091 0.648 -0.126 Q 0.71 -0.29 0.71 -0.35000000000000003 Q 0.71 -0.42 0.656 -0.42 Q 0.5680000000000001 -0.42 0.51 -0.325 Q 0.494 -0.3 0.491 -0.28700000000000003 Q 0.488 -0.274 0.431 -0.046 Q 0.42 -0.003 0.406 0.006 L 0.405 0.007 L 0.404 0.007 Q 0.397 0.011 0.386 0.011 Q 0.361 0.006 0.357 -0.016 L 0.37 -0.075 L 0.39 -0.151 L 0.418 -0.267 Q 0.433 -0.323 0.433 -0.35000000000000003 Q 0.433 -0.42 0.379 -0.42 Q 0.289 -0.42 0.228 -0.32 Q 0.221 -0.309 0.216 -0.298 L 0.203 -0.248 Q 0.198 -0.225 0.186 -0.181 L 0.164 -0.091 Q 0.147 -0.022 0.145 -0.017 Q 0.136 0.009000000000000001 0.108 0.011 Q 0.083 0.006 0.079 -0.016 Q 0.079 -0.014 0.088 -0.059000000000000004 Z" />
            </g>
            <g transform="translate(4.0208571428571425,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.088 -0.059000000000000004 L 0.088 -0.059000000000000004 L 0.146 -0.29 Q 0.162 -0.354 0.162 -0.374 Q 0.162 -0.42 0.131 -0.42 Q 0.09 -0.42 0.064 -0.325 Q 0.061 -0.314 0.058 -0.301 Q 0.047 -0.279 0.041 -0.278 Q 0.029 -0.278 0.029 -0.28800000000000003 Q 0.029 -0.304 0.048 -0.356 Q 0.054 -0.373 0.06 -0.385 Q 0.084 -0.434 0.12 -0.441 Q 0.127 -0.442 0.134 -0.442 Q 0.188 -0.442 0.214 -0.397 Q 0.224 -0.378 0.226 -0.355 Q 0.29 -0.442 0.382 -0.442 Q 0.433 -0.442 0.464 -0.41600000000000004 Q 0.492 -0.392 0.496 -0.34500000000000003 Q 0.5640000000000001 -0.442 0.659 -0.442 Q 0.748 -0.442 0.769 -0.373 L 0.769 -0.372 Q 0.774 -0.355 0.774 -0.335 Q 0.774 -0.28200000000000003 0.713 -0.115 Q 0.6950000000000001 -0.07100000000000001 0.6950000000000001 -0.044 Q 0.6950000000000001 -0.011 0.719 -0.011 Q 0.781 -0.011 0.8170000000000001 -0.121 L 0.8210000000000001 -0.135 Q 0.8260000000000001 -0.151 0.8310000000000001 -0.152 L 0.8310000000000001 -0.153 L 0.836 -0.153 Q 0.848 -0.153 0.848 -0.14300000000000002 Q 0.848 -0.126 0.8270000000000001 -0.08 Q 0.784 0.011 0.717 0.011 Q 0.666 0.011 0.644 -0.032 Q 0.635 -0.05 0.635 -0.07100000000000001 Q 0.635 -0.091 0.648 -0.126 Q 0.71 -0.29 0.71 -0.35000000000000003 Q 0.71 -0.42 0.656 -0.42 Q 0.5680000000000001 -0.42 0.51 -0.325 Q 0.494 -0.3 0.491 -0.28700000000000003 Q 0.488 -0.274 0.431 -0.046 Q 0.42 -0.003 0.406 0.006 L 0.405 0.007 L 0.404 0.007 Q 0.397 0.011 0.386 0.011 Q 0.361 0.006 0.357 -0.016 L 0.37 -0.075 L 0.39 -0.151 L 0.418 -0.267 Q 0.433 -0.323 0.433 -0.35000000000000003 Q 0.433 -0.42 0.379 -0.42 Q 0.289 -0.42 0.228 -0.32 Q 0.221 -0.309 0.216 -0.298 L 0.203 -0.248 Q 0.198 -0.225 0.186 -0.181 L 0.164 -0.091 Q 0.147 -0.022 0.145 -0.017 Q 0.136 0.009000000000000001 0.108 0.011 Q 0.083 0.006 0.079 -0.016 Q 0.079 -0.014 0.088 -0.059000000000000004 Z" />
            </g>
            <g transform="translate(5.17663492063492,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.687 -0.327 L 0.09 -0.327 Q 0.058 -0.327 0.056 -0.34700000000000003 Q 0.056 -0.367 0.089 -0.367 L 0.6880000000000001 -0.367 Q 0.72 -0.367 0.721 -0.34700000000000003 Q 0.721 -0.327 0.687 -0.327 M 0.6880000000000001 -0.133 L 0.089 -0.133 Q 0.057 -0.133 0.056 -0.153 Q 0.056 -0.17300000000000001 0.09 -0.17300000000000001 L 0.687 -0.17300000000000001 Q 0.719 -0.17300000000000001 0.721 -0.153 Q 0.721 -0.133 0.6880000000000001 -0.133 Z" />
            </g>
            <g transform="translate(6.2324126984126975,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.28400000000000003 -0.625 Q 0.28400000000000003 -0.594 0.253 -0.578 Q 0.242 -0.5720000000000001 0.231 -0.5720000000000001 Q 0.201 -0.5720000000000001 0.194 -0.599 Q 0.193 -0.604 0.193 -0.608 Q 0.193 -0.636 0.221 -0.653 Q 0.234 -0.661 0.247 -0.661 Q 0.277 -0.661 0.28300000000000003 -0.634 L 0.28300000000000003 -0.633 Q 0.28400000000000003 -0.629 0.28400000000000003 -0.625 M 0.20800000000000002 -0.249 L 0.169 -0.14400000000000002 Q 0.14100000000000001 -0.063 0.14 -0.044 Q 0.14 -0.011 0.164 -0.011 Q 0.227 -0.011 0.262 -0.123 L 0.266 -0.135 Q 0.271 -0.151 0.276 -0.152 L 0.276 -0.153 L 0.281 -0.153 Q 0.293 -0.153 0.293 -0.14300000000000002 Q 0.293 -0.125 0.271 -0.079 Q 0.228 0.011 0.162 0.011 Q 0.109 0.011 0.088 -0.033 Q 0.08 -0.051000000000000004 0.08 -0.07100000000000001 Q 0.08 -0.091 0.10300000000000001 -0.151 Q 0.17300000000000001 -0.339 0.17200000000000001 -0.336 Q 0.183 -0.364 0.183 -0.388 Q 0.183 -0.42 0.158 -0.42 Q 0.10300000000000001 -0.42 0.068 -0.329 L 0.068 -0.328 Q 0.062 -0.313 0.057 -0.296 Q 0.046 -0.279 0.041 -0.278 Q 0.029 -0.278 0.029 -0.28800000000000003 Q 0.029 -0.306 0.051000000000000004 -0.35100000000000003 Q 0.094 -0.442 0.161 -0.442 Q 0.214 -0.442 0.23500000000000001 -0.397 Q 0.243 -0.38 0.243 -0.36 Q 0.243 -0.34 0.23 -0.307 Q 0.213 -0.261 0.20800000000000002 -0.249 Z" />
            </g>
            <g transform="translate(6.577412698412697,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.088 -0.059000000000000004 L 0.088 -0.059000000000000004 L 0.146 -0.29 Q 0.162 -0.354 0.162 -0.374 Q 0.162 -0.42 0.131 -0.42 Q 0.09 -0.42 0.064 -0.325 Q 0.061 -0.314 0.058 -0.301 Q 0.047 -0.279 0.041 -0.278 Q 0.029 -0.278 0.029 -0.28800000000000003 Q 0.029 -0.304 0.049 -0.36 L 0.049 -0.361 Q 0.049 -0.362 0.05 -0.362 L 0.05 -0.363 L 0.05 -0.364 L 0.059000000000000004 -0.385 Q 0.081 -0.425 0.10400000000000001 -0.435 Q 0.11800000000000001 -0.442 0.134 -0.442 Q 0.19 -0.442 0.215 -0.395 Q 0.224 -0.377 0.226 -0.355 Q 0.29 -0.442 0.382 -0.442 Q 0.468 -0.442 0.491 -0.376 Q 0.497 -0.358 0.497 -0.335 Q 0.497 -0.28400000000000003 0.436 -0.115 Q 0.418 -0.07100000000000001 0.418 -0.044 Q 0.418 -0.011 0.442 -0.011 Q 0.508 -0.011 0.544 -0.135 Q 0.549 -0.151 0.554 -0.152 L 0.554 -0.153 L 0.559 -0.153 Q 0.5710000000000001 -0.153 0.5710000000000001 -0.14300000000000002 Q 0.5710000000000001 -0.126 0.55 -0.08 Q 0.507 0.011 0.44 0.011 Q 0.389 0.011 0.367 -0.032 Q 0.358 -0.05 0.358 -0.07100000000000001 Q 0.358 -0.091 0.368 -0.117 Q 0.433 -0.292 0.433 -0.35000000000000003 Q 0.433 -0.42 0.379 -0.42 Q 0.289 -0.42 0.228 -0.32 Q 0.221 -0.309 0.216 -0.298 L 0.203 -0.248 Q 0.198 -0.225 0.186 -0.181 L 0.164 -0.091 Q 0.147 -0.022 0.145 -0.017 Q 0.136 0.009000000000000001 0.108 0.011 Q 0.083 0.006 0.079 -0.016 Q 0.079 -0.014 0.088 -0.059000000000000004 Z" />
            </g>
            <g transform="translate(7.399634920634919,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.261 -0.229 L 0.261 -0.229 L 0.275 -0.07100000000000001 Q 0.278 -0.034 0.249 -0.034 Q 0.231 -0.034 0.225 -0.05 Q 0.223 -0.055 0.224 -0.061 L 0.224 -0.066 L 0.23800000000000002 -0.23 L 0.112 -0.137 Q 0.1 -0.128 0.092 -0.128 Q 0.074 -0.128 0.067 -0.146 Q 0.065 -0.151 0.065 -0.156 Q 0.065 -0.17300000000000001 0.08600000000000001 -0.182 L 0.227 -0.25 L 0.08600000000000001 -0.317 Q 0.065 -0.326 0.065 -0.343 Q 0.065 -0.362 0.084 -0.369 Q 0.088 -0.371 0.092 -0.371 Q 0.098 -0.371 0.112 -0.361 L 0.23800000000000002 -0.269 L 0.224 -0.428 L 0.224 -0.439 Q 0.22 -0.455 0.23800000000000002 -0.462 L 0.23900000000000002 -0.463 L 0.24 -0.463 Q 0.245 -0.465 0.25 -0.465 Q 0.278 -0.465 0.275 -0.429 L 0.261 -0.269 L 0.387 -0.362 Q 0.399 -0.371 0.40700000000000003 -0.371 Q 0.425 -0.371 0.432 -0.353 Q 0.434 -0.34800000000000003 0.434 -0.343 Q 0.434 -0.326 0.41300000000000003 -0.317 L 0.271 -0.249 L 0.41600000000000004 -0.181 Q 0.434 -0.17200000000000001 0.434 -0.156 Q 0.434 -0.137 0.41500000000000004 -0.13 Q 0.41100000000000003 -0.128 0.40700000000000003 -0.128 Q 0.399 -0.128 0.37 -0.15 L 0.261 -0.229 Z" />
            </g>
            <g transform="translate(8.121857142857142,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.449 -0.17400000000000002 L 0.449 -0.17400000000000002 L 0.421 0 L 0.05 0 Q 0.05 -0.025 0.056 -0.032 L 0.061 -0.037 L 0.253 -0.251 Q 0.358 -0.369 0.358 -0.47200000000000003 Q 0.358 -0.561 0.304 -0.607 L 0.303 -0.608 L 0.302 -0.608 Q 0.27 -0.635 0.224 -0.635 Q 0.152 -0.635 0.109 -0.5730000000000001 Q 0.097 -0.555 0.089 -0.533 Q 0.092 -0.534 0.10200000000000001 -0.534 Q 0.14200000000000002 -0.534 0.153 -0.498 L 0.153 -0.497 Q 0.155 -0.49 0.155 -0.482 Q 0.155 -0.442 0.117 -0.431 Q 0.109 -0.429 0.10300000000000001 -0.429 Q 0.066 -0.429 0.054 -0.46 Q 0.05 -0.47100000000000003 0.05 -0.485 Q 0.05 -0.562 0.109 -0.617 Q 0.162 -0.666 0.23700000000000002 -0.666 Q 0.34700000000000003 -0.666 0.40800000000000003 -0.592 Q 0.443 -0.549 0.448 -0.49 Q 0.449 -0.481 0.449 -0.47200000000000003 Q 0.449 -0.399 0.394 -0.333 Q 0.365 -0.299 0.304 -0.243 L 0.243 -0.189 L 0.233 -0.18 L 0.127 -0.077 L 0.307 -0.077 Q 0.395 -0.077 0.402 -0.085 Q 0.41200000000000003 -0.099 0.424 -0.17400000000000002 L 0.449 -0.17400000000000002 Z" />
            </g>
            <g transform="translate(8.621857142857142,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.449 -0.201 L 0.449 -0.201 Q 0.449 -0.098 0.372 -0.031 Q 0.31 0.022 0.231 0.022 Q 0.139 0.022 0.085 -0.055 Q 0.05 -0.10400000000000001 0.05 -0.161 Q 0.05 -0.20700000000000002 0.091 -0.213 Q 0.095 -0.214 0.099 -0.214 Q 0.136 -0.214 0.147 -0.181 Q 0.149 -0.17300000000000001 0.149 -0.165 Q 0.149 -0.133 0.121 -0.121 Q 0.111 -0.116 0.099 -0.116 Q 0.088 -0.116 0.082 -0.11800000000000001 Q 0.1 -0.055 0.159 -0.024 Q 0.192 -0.006 0.229 -0.006 Q 0.296 -0.006 0.336 -0.07100000000000001 Q 0.36 -0.113 0.36 -0.20800000000000002 Q 0.36 -0.292 0.342 -0.334 Q 0.314 -0.398 0.257 -0.398 Q 0.176 -0.398 0.128 -0.328 Q 0.122 -0.319 0.115 -0.319 Q 0.10200000000000001 -0.319 0.101 -0.333 L 0.101 -0.34500000000000003 L 0.101 -0.641 Q 0.101 -0.664 0.111 -0.665 Q 0.115 -0.665 0.123 -0.662 Q 0.186 -0.634 0.256 -0.634 Q 0.328 -0.634 0.392 -0.663 Q 0.397 -0.666 0.4 -0.666 Q 0.41000000000000003 -0.666 0.41000000000000003 -0.655 Q 0.41000000000000003 -0.642 0.378 -0.613 Q 0.31 -0.551 0.217 -0.551 Q 0.17500000000000002 -0.551 0.132 -0.5640000000000001 L 0.132 -0.369 Q 0.184 -0.42 0.259 -0.42 Q 0.34700000000000003 -0.42 0.404 -0.343 Q 0.449 -0.28200000000000003 0.449 -0.201 Z" />
            </g>
            <g transform="translate(9.121857142857142,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.18 -0.083 L 0.18 0 L 0.097 0 L 0.097 -0.083 L 0.18 -0.083 Z" />
            </g>
            <g transform="translate(9.399857142857142,12.46952380952381)">
              <path fill="rgb(0,0,0)" stroke="none" paintOrder="stroke fill markers"
                    d=" M 0.028 -0.165 L 0.028 -0.196 L 0.335 -0.665 Q 0.343 -0.677 0.355 -0.677 Q 0.369 -0.677 0.371 -0.666 L 0.371 -0.651 L 0.371 -0.196 L 0.47100000000000003 -0.196 L 0.47100000000000003 -0.165 L 0.371 -0.165 L 0.371 -0.078 Q 0.371 -0.047 0.383 -0.04 Q 0.397 -0.031 0.447 -0.031 L 0.468 -0.031 L 0.468 0 Q 0.427 -0.003 0.332 -0.003 Q 0.23800000000000002 -0.003 0.197 0 L 0.197 -0.031 L 0.218 -0.031 Q 0.28 -0.031 0.29 -0.048 Q 0.294 -0.055 0.294 -0.078 L 0.294 -0.165 L 0.028 -0.165 M 0.3 -0.196 L 0.3 -0.5690000000000001 L 0.056 -0.196 L 0.3 -0.196 Z" />
            </g>
          </g>
        </g>
      </g>
    </svg>
  </div>;

  return (<>
    <CalculatorLayout>
      <ToolHeaderNew
        name={number ? t(`${TKEY}.question_n`, { count: number }) : meta.title}
        description={meta.description}
      />

      {result !== null && !error ? (
        <Result color="positive">
          <H3 className="first-letter:capitalize">{t("answer")}:</H3>

          <div className="mt-3 mb-8 text-center">
            <strong className="mt-8 text-3xl first-letter:capitalize"> {result} {t(`${TKEY}.unit`)}</strong>
          </div>
          <div className="mt-3 mb-8 text-center">
            {t(`${TKEY}.result`, { from: number, to: result })}
          </div>
          {result !== notRoundedResult ? (
            <div className="mt-3 mb-8 text-center">
              {t(`${TKEY}.result_is_rounded`)} <br />
              {t(`${TKEY}.precise_value`, { result: notRoundedResult })}
            </div>
          ) : null}
        </Result>
      ) : null}

      <ToolForm>
        <LengthCalculator
          from={Unit.inches}
          to={Unit.mm}
          value={number ?? 1}
        />
      </ToolForm>

      {error ?
        <Result color="negative">
          <p>{error}</p>
        </Result>
        : null}
    </CalculatorLayout>

    <AdditionalContentLayout>
      <AdditionalInfo>
        {number !== null ? <>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_to_convert_n`, { count: number })}</H2>
            <p>{t(`${TKEY}.info_h_how_to_convert_n_formula`)}</p>
            <div className="flex justify-center mb-4">
              {formulaSvg}
            </div>
            <p>{t(`${TKEY}.info_h_how_to_convert_n_expl`, { count: number })}</p>
            <p className="text-center">{number} in * 25.4 = {result} mm</p>
          </AdditionalInfoSection>
        </> : null}


        {number !== null ? null : <>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_to_convert`)}</H2>
            <p>{t(`${TKEY}.info_p_how_to_convert`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_many_units_in_one_inch`)}</H2>
            <p>{t(`${TKEY}.info_p_how_many_units_in_one_inch`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_many_units_in_one_half_inch`)}</H2>
            <p>{t(`${TKEY}.info_p_how_many_units_in_one__half_inch`)}</p>
          </AdditionalInfoSection>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
            <p>{t(`${TKEY}.info_p_common_numbers`)}</p>
            <table className="w-full table table-fixed text-center border-collapse border border-gray-400 rounded-md">
              <thead>
              <tr>
                <th className="font-bold px-3 py-2 border-b border-r border-gray-400">{t(`${TKEY}.inches`)}</th>
                <th className="font-bold px-3 py-2 border-b border-gray-400">{t(`${TKEY}.unit_full`)}</th>
              </tr>
              </thead>
              <tbody>
              {commonValues.map((inches) => (
                <tr key={inches} className="hover:bg-slate-200 dark:hover:bg-slate-700">
                  <td className="px-3 py-2 border-b border-r border-gray-400">{inches}</td>
                  <td className="px-3 py-2 border-b border-gray-400">{inchesToMm(inches)}</td>
                </tr>
              ))}
              </tbody>
            </table>
          </AdditionalInfoSection>
        </>}
      </AdditionalInfo>

      <Author lastUpdated={LAST_UPDATED} />
    </AdditionalContentLayout>
  </>);
}


